import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  DeleteOutline as DeletedIcon,
  Restore as RestoreIcon,
  DeleteForever as PurgedIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Entry {
  id: string;
  post_id: string;
  action: 'deleted' | 'restored' | 'purged';
  actor: string;
  content: string | null;
  platforms: string[];
  status_when_deleted: string | null;
  source: string;
  at: string;
  /** Whether the post can still be brought back. */
  current_state: 'recoverable' | 'restored' | 'purged';
}

const when = (stamp: string) => {
  // SQLite CURRENT_TIMESTAMP is UTC with no zone marker
  const iso = stamp.includes('T') ? stamp : stamp.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? stamp
    : d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};

const ACTION = {
  deleted: { label: 'Deleted', colour: 'error' as const, icon: <DeletedIcon fontSize="small" /> },
  restored: { label: 'Restored', colour: 'success' as const, icon: <RestoreIcon fontSize="small" /> },
  purged: { label: 'Purged', colour: 'default' as const, icon: <PurgedIcon fontSize="small" /> },
};

/**
 * Who deleted which post, when, and whether it can still be brought back.
 *
 * `deleted_at` on the row records the time but not the actor, and a restore clears it — so
 * without this log a deletion leaves no trace once undone. The content snapshot is the point:
 * an audit trail exists to answer "what was in that post" after the post is gone.
 *
 * Admin-only, because it exposes the content of posts other people removed.
 */
export const DeletionHistory: React.FC<{
  onRestored?: () => void;
  /** Open a deleted post's full card. Omitted where there is nothing to open into. */
  onOpenPost?: (postId: string) => void;
}> = ({ onRestored, onOpenPost }) => {
  const { token } = useAuth();
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/post-deletions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      setEntries(await r.json());
    } catch {
      setError('Could not load deletion history');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (postId: string) => {
    setBusy(postId);
    setError('');
    setNotice('');
    try {
      const r = await fetch(`${API_URL}/api/posts/${postId}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await r.json();
      if (!r.ok) {
        setError(body.message || body.error || 'Could not restore');
        return;
      }
      setNotice('Post restored — it is back in Post History.');
      load();
      // The restored post now belongs in the list below, which has its own state
      onRestored?.();
    } catch {
      setError('Network error restoring the post');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Deleted posts</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Who removed what, and whether it can still be brought back. Deleted posts are kept for
          30 days, then permanently purged.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

        {!entries?.length ? (
          <Typography variant="body2" color="text.secondary">
            Nothing has been deleted.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {entries.map((e, i) => {
              const meta = ACTION[e.action] || ACTION.deleted;
              return (
                <Box
                  key={e.id}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                    py: 1.5,
                    borderTop: i ? 1 : 0,
                    borderColor: 'divider',
                  }}
                >
                  <Chip
                    size="small"
                    icon={meta.icon}
                    label={meta.label}
                    color={meta.colour}
                    variant={meta.colour === 'default' ? 'outlined' : 'filled'}
                    sx={{ minWidth: 104 }}
                  />

                  <Box
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      // The snapshot is clamped to one line, so the full post is only
                      // reachable by opening it. Purged posts have no row left to open.
                      cursor: e.current_state === 'purged' ? 'default' : 'pointer',
                    }}
                    onClick={() => e.current_state !== 'purged' && onOpenPost?.(e.post_id)}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        ...(e.current_state !== 'purged' && {
                          '&:hover': { textDecoration: 'underline' },
                        }),
                      }}
                    >
                      {e.content || <em>no content recorded</em>}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {when(e.at)} · {e.actor}
                      {e.platforms.length > 0 && ` · ${e.platforms.map(getPlatformName).join(', ')}`}
                      {e.status_when_deleted && ` · was ${e.status_when_deleted}`}
                      {e.source === 'api' && ' · via API'}
                    </Typography>
                  </Box>

                  {/* Only offer a restore where one is actually possible */}
                  {e.action === 'deleted' && e.current_state === 'recoverable' && (
                    <Button
                      size="small"
                      startIcon={<RestoreIcon fontSize="small" />}
                      disabled={busy === e.post_id}
                      onClick={() => restore(e.post_id)}
                    >
                      Restore
                    </Button>
                  )}
                  {e.current_state === 'purged' && e.action !== 'purged' && (
                    <Tooltip title="Kept for 30 days after deletion, then permanently removed" arrow>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mt: 0.75 }}>
                        past recovery
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
